/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, FC, useContext, useEffect, useState } from 'react';

export interface PageLink {
	title: string;
	path: string;
	isActive: boolean;
	isSeparator?: boolean;
}

export interface PageDataContextModel {
	pageTitle?: string;
	setPageTitle: (_title: string) => void;
	pageDescription?: string;
	setPageDescription: (_description: string) => void;
	pageBreadcrumbs?: Array<PageLink>;
	setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void;
}

const PageDataContext = createContext<PageDataContextModel>({
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	setPageTitle: (_title: string) => void 0,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void 0,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	setPageDescription: (_description: string) => void 0
});

const PageDataProvider = ({ children }: {children: React.ReactElement}): JSX.Element => {
	const [pageTitle, setPageTitle] = useState<string>('');
	const [pageDescription, setPageDescription] = useState<string>('');
	const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([]);
	const value: PageDataContextModel = {
		pageTitle,
		setPageTitle,
		pageDescription,
		setPageDescription,
		pageBreadcrumbs,
		setPageBreadcrumbs
	};
	return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>;
};

function usePageData(): PageDataContextModel {
	return useContext(PageDataContext);
}

type Props = {
	description?: string;
	breadcrumbs?: Array<PageLink>;
	children?: React.ReactElement | string | number;
}

const PageTitle: FC<Props> = ({ children, description, breadcrumbs }) => {
	const { setPageTitle, setPageDescription, setPageBreadcrumbs } = usePageData();
	useEffect(() => {
		if (children) {
			setPageTitle(children.toString());
		}
		return (): void => {
			setPageTitle('');
		};
	}, [children]);

	useEffect(() => {
		if (description) {
			setPageDescription(description);
		}
		return (): void => {
			setPageDescription('');
		};
	}, [description]);

	useEffect(() => {
		if (breadcrumbs) {
			setPageBreadcrumbs(breadcrumbs);
		}
		return (): void => {
			setPageBreadcrumbs([]);
		};
	}, [breadcrumbs]);

	return <></>;
};

const PageDescription: React.FC = ({ children }: {children: React.ReactElement}) => {
	const { setPageDescription } = usePageData();
	useEffect(() => {
		if (children) {
			setPageDescription(children.toString());
		}
		return (): void => {
			setPageDescription('');
		};
	}, [children]);
	return <></>;
};

export const checkIfValidUUID = (value: string): boolean => {
	// Regex to validate both UUID v4 and v7
	const regexExp = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-(4[0-9a-fA-F]{3}|7[0-9a-fA-F]{3})-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

	return regexExp.test(value);
};

export const validateEmail = (value: string): boolean => {
	// Regular expression for email validation
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailRegex.test(value);
};

export { PageDescription, PageTitle, PageDataProvider, usePageData };
